import { useState, useEffect } from 'react';
import { Loader2, Play, Coins, ChevronLeft, ChevronRight, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminListMintQueue, adminMintQueueItem, adminProcessMintQueue } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';

interface MintQueueItem {
  id: number;
  vaultId: number;
  userId: number;
  status: string;
  createdAt: number;
  processedAt: number | null;
  vaultName: string;
  coverUrl: string | null;
  userEmail: string;
  userName: string;
  artistName: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function AdminMintQueuePage() {
  const { token } = useAuthStore();
  const [items, setItems] = useState<MintQueueItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [mintingId, setMintingId] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{ minted: number; failed: number; processed: number } | null>(null);

  const fetchQueue = async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminListMintQueue({ page, status: statusFilter }, token!);
      if (res.ok) {
        const json = await res.json() as { data: MintQueueItem[]; pagination: Pagination };
        setItems(json.data);
        setPagination(json.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch mint queue:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, [statusFilter]);

  const handleMintSingle = async (queueId: number) => {
    setMintingId(queueId);
    try {
      const res = await adminMintQueueItem(queueId, token!);
      if (res.ok) {
        // Remove from list or refresh
        setItems(prev => prev.filter(i => i.id !== queueId));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      } else {
        const errJson = await res.json() as { error: string };
        alert(`Mint failed: ${errJson.error}`);
      }
    } catch {
      alert('Failed to mint');
    } finally {
      setMintingId(null);
    }
  };

  const handleProcessAll = async () => {
    setProcessing(true);
    setProcessResult(null);
    try {
      const res = await adminProcessMintQueue(token!);
      if (res.ok) {
        const json = await res.json() as { success: boolean; processed: number; minted: number; failed: number };
        setProcessResult({ processed: json.processed, minted: json.minted, failed: json.failed });
        // Refresh the list
        fetchQueue(pagination.page);
      } else {
        alert('Failed to process queue');
      }
    } catch {
      alert('Failed to process queue');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (ts: number): string => {
    return new Date(ts * 1000).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-deep-charcoal text-white">
      <AdminDashboardNav currentPage="mint-queue" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold">Mint Queue</h1>
            <p className="text-text-secondary text-sm mt-1">
              {pagination.total} {statusFilter} item{pagination.total !== 1 ? 's' : ''} in queue
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-electric-cyan/50"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>

            {/* Process all button */}
            {statusFilter === 'pending' && (
              <button
                onClick={handleProcessAll}
                disabled={processing || pagination.total === 0}
                className="flex items-center gap-2 px-4 py-2 bg-electric-cyan/10 border border-electric-cyan/30 text-electric-cyan rounded-lg text-sm font-mono hover:bg-electric-cyan/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Process All
              </button>
            )}
          </div>
        </div>

        {/* Process result banner */}
        {processResult && (
          <div className="mb-6 p-4 rounded-lg border border-electric-cyan/30 bg-electric-cyan/5">
            <div className="flex items-center gap-2 text-sm font-mono">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span>
                Processed <strong>{processResult.processed}</strong> items —{' '}
                <span className="text-green-400">{processResult.minted} minted</span>
                {processResult.failed > 0 && (
                  <span className="text-red-400">, {processResult.failed} failed</span>
                )}
              </span>
              <button
                onClick={() => setProcessResult(null)}
                className="ml-auto text-text-muted hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-electric-cyan" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-text-muted">
            <Coins className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-mono text-sm">No {statusFilter} items in the queue</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Vault</th>
                  <th className="text-left py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Artist</th>
                  <th className="text-left py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Fan Email</th>
                  <th className="text-left py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Queued At</th>
                  <th className="text-left py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Status</th>
                  {statusFilter === 'pending' && (
                    <th className="text-right py-3 px-3 text-xs font-mono text-text-muted uppercase tracking-wider">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    {/* Vault cover + name */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-surface border border-white/10 overflow-hidden flex-shrink-0">
                          {item.coverUrl ? (
                            <img src={item.coverUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Coins className="w-4 h-4 text-text-muted" />
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-medium truncate max-w-[180px]">{item.vaultName}</span>
                      </div>
                    </td>

                    {/* Artist name */}
                    <td className="py-3 px-3">
                      <span className="text-sm text-text-secondary">{item.artistName}</span>
                    </td>

                    {/* User email */}
                    <td className="py-3 px-3">
                      <span className="text-sm text-text-secondary font-mono">{item.userEmail}</span>
                    </td>

                    {/* Created at */}
                    <td className="py-3 px-3">
                      <span className="text-sm text-text-muted font-mono">{formatDate(item.createdAt)}</span>
                    </td>

                    {/* Status badge */}
                    <td className="py-3 px-3">
                      {item.status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <AlertCircle className="w-3 h-3" /> pending
                        </span>
                      )}
                      {item.status === 'completed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3" /> completed
                        </span>
                      )}
                      {item.status === 'failed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border bg-red-500/20 text-red-400 border-red-500/30">
                          <XCircle className="w-3 h-3" /> failed
                        </span>
                      )}
                    </td>

                    {/* Action */}
                    {statusFilter === 'pending' && (
                      <td className="py-3 px-3 text-right">
                        <button
                          onClick={() => handleMintSingle(item.id)}
                          disabled={mintingId === item.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-electric-cyan/10 border border-electric-cyan/30 text-electric-cyan rounded text-xs font-mono hover:bg-electric-cyan/20 transition-colors disabled:opacity-50"
                        >
                          {mintingId === item.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Coins className="w-3 h-3" />
                          )}
                          Mint
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => fetchQueue(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-mono text-text-muted hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm font-mono text-text-secondary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => fetchQueue(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-mono text-text-muted hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
